/**
 * useCredenciales.js - Hook para gestión de plantillas de credenciales
 *
 * Maneja todas las operaciones CRUD con el backend de credenciales
 */

import { useState, useCallback } from 'react';
import { getAuthHeaders } from '../../../services/api/utils/fetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export function useCredenciales() {
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaActual, setPlantillaActual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar todas las plantillas
  const loadPlantillas = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/plantillas`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Error cargando plantillas');
      }

      const data = await response.json();
      setPlantillas(data.data || []);
      return data.data;
    } catch (err) {
      setError(err.message);
      console.error('[useCredenciales] Error:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar una plantilla específica
  const loadPlantilla = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/plantillas/${id}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Plantilla no encontrada');
      }

      const data = await response.json();
      setPlantillaActual(data.data);
      return data.data;
    } catch (err) {
      setError(err.message);
      console.error('[useCredenciales] Error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear nueva plantilla
  const crearPlantilla = useCallback(async (nombre, tipo) => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/plantillas`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nombre, tipo })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error creando plantilla');
      }

      const data = await response.json();
      setPlantillas(prev => [data.data, ...prev]);
      return data.data;
    } catch (err) {
      setError(err.message);
      console.error('[useCredenciales] Error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Actualizar plantilla (campos, nombre, etc.)
  const actualizarPlantilla = useCallback(async (id, datos) => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/plantillas/${id}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(datos)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error actualizando plantilla');
      }

      const data = await response.json();

      // Actualizar en la lista
      setPlantillas(prev => prev.map(p =>
        p.id === id ? { ...p, ...data.data } : p
      ));

      // Actualizar plantilla actual si es la misma
      if (plantillaActual?.id === id) {
        setPlantillaActual(prev => ({ ...prev, ...data.data }));
      }

      return data.data;
    } catch (err) {
      setError(err.message);
      console.error('[useCredenciales] Error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [plantillaActual]);

  // Subir imagen a una plantilla
  const subirImagen = useCallback(async (id, file) => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      // Remover Content-Type para que el navegador lo establezca con boundary
      delete headers['Content-Type'];

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/credenciales/plantillas/${id}/imagen`, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error subiendo imagen');
      }

      const data = await response.json();

      // Actualizar en la lista
      setPlantillas(prev => prev.map(p =>
        p.id === id ? { ...p, imagenUrl: data.data.imagenUrl } : p
      ));

      // Actualizar plantilla actual si es la misma
      if (plantillaActual?.id === id) {
        setPlantillaActual(prev => ({ ...prev, imagenUrl: data.data.imagenUrl }));
      }

      return data.data;
    } catch (err) {
      setError(err.message);
      console.error('[useCredenciales] Error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [plantillaActual]);

  // Eliminar plantilla
  const eliminarPlantilla = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/plantillas/${id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error eliminando plantilla');
      }

      // Quitar de la lista
      setPlantillas(prev => prev.filter(p => p.id !== id));

      // Limpiar plantilla actual si es la misma
      if (plantillaActual?.id === id) {
        setPlantillaActual(null);
      }

      return true;
    } catch (err) {
      setError(err.message);
      console.error('[useCredenciales] Error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [plantillaActual]);

  // Regenerar URLs firmadas de todas las plantillas
  const regenerarUrls = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/regenerar-urls`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error regenerando URLs');
      }

      const data = await response.json();

      // Recargar plantillas para obtener las nuevas URLs
      await loadPlantillas();

      return {
        success: true,
        actualizadas: data.actualizadas,
        errores: data.errores
      };
    } catch (err) {
      setError(err.message);
      console.error('[useCredenciales] Error:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [loadPlantillas]);

  // Verificar si un folio existe y tiene código rascable
  const verificarFolio = useCallback(async (folio) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/verificar-folio/${folio}`, {
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { existe: false, error: errorData.error };
      }

      const data = await response.json();
      return data.data;
    } catch (err) {
      console.error('[useCredenciales] Error verificando folio:', err);
      return { existe: false, error: err.message };
    }
  }, []);

  // Regenerar código rascable para un folio
  const regenerarCodigo = useCallback(async (folio) => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/regenerar-codigo/${folio}`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error regenerando código');
      }

      const data = await response.json();
      return data.data;
    } catch (err) {
      setError(err.message);
      console.error('[useCredenciales] Error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Generar PDF con credenciales
  const generarPDF = useCallback(async (plantillaFrenteId, plantillaReversoId, folios) => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/credenciales/generar-pdf`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plantillaFrenteId: plantillaFrenteId || undefined,
          plantillaReversoId: plantillaReversoId || undefined,
          folios
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error generando PDF');
      }

      // Obtener información de errores del header
      const erroresCount = parseInt(response.headers.get('X-Credenciales-Errores-Count') || '0');
      const generadas = parseInt(response.headers.get('X-Credenciales-Generadas') || '0');

      // Descargar el PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credenciales_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return {
        success: true,
        generadas,
        errores: erroresCount
      };
    } catch (err) {
      setError(err.message);
      console.error('[useCredenciales] Error:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    plantillas,
    plantillaActual,
    loading,
    error,
    loadPlantillas,
    loadPlantilla,
    crearPlantilla,
    actualizarPlantilla,
    subirImagen,
    eliminarPlantilla,
    regenerarUrls,
    generarPDF,
    verificarFolio,
    regenerarCodigo,
    setPlantillaActual,
    setError
  };
}
